/**
 * research-panel.ts — modal overlay for M12 research.
 *
 * Opened by R (toggle) or programmatically; closed by R, Escape, or
 * clicking the backdrop. Shows:
 *   - Active research (tech name + rpAccumulated / researchCost text).
 *   - **v3M23.1** — full tech tree as an SVG DAG. Categories sit on the
 *     Y axis (one row band per category, with vertical sub-stacking
 *     when multiple techs share a tier inside a category); tier sits
 *     on the X axis (Mk 1 leftmost, Mk 6 or deepest-prereq rightmost).
 *     Prerequisite edges drawn as cubic Bézier curves with arrow heads
 *     at the destination. Researched / active / available / locked
 *     visual states preserved from the prior list view; click an
 *     available node to dispatch `setResearchTarget`. Replaces the
 *     pre-v3M23 vertical list which buried prereq structure inside
 *     category-then-level sorting.
 *
 * Tick-rate re-render matches detail-panel: the panel's content div is
 * rebuilt on every `update()` call. Event delegation via `pointerdown`
 * (not `click`) avoids the same mid-press rebuild race noted in
 * detail-panel.ts. Keyboard activation is deferred — a R-key-opens-panel
 * listener exists, but Enter/Space on a focused row would not fire
 * pointerdown; polish pass if/when needed.
 */

import type { Command } from '../game/commands';
import type { Empire, GameState, TechDef } from '../state/types/index';
import { getAudioEngine } from '../audio';

const SVG_NS = 'http://www.w3.org/2000/svg';

// ---------------------------------------------------------------------------
// v3M23.1 — DAG layout constants
// ---------------------------------------------------------------------------

/** Tech-node footprint in SVG coords (matches the prior list-row width feel). */
const NODE_W = 150;
const NODE_H = 36;
/** Horizontal gap between tier columns. */
const COL_GAP = 32;
/** Vertical gap between rows (within a category band, and between categories). */
const ROW_GAP = 8;
/** Left padding for the category label band. */
const LABEL_W = 132;
/** SVG canvas margin inside the panel. */
const SVG_PAD = 12;

export interface ResearchPanelHandle {
  update(state: GameState): void;
  destroy(): void;
  open(): void;
  close(): void;
  toggle(): void;
  isOpen(): boolean;
}

export interface ResearchPanelOptions {
  queueCommand?: (cmd: Command) => void;
}

const PANEL_WIDTH_PX = 960;

export function mountResearchPanel(
  parent: HTMLElement,
  options: ResearchPanelOptions = {},
): ResearchPanelHandle {
  const backdrop = document.createElement('div');
  Object.assign(backdrop.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'rgba(2, 6, 14, 0.55)',
    display: 'none',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '48px',
    zIndex: '50',
    pointerEvents: 'auto',
  });

  const panel = document.createElement('div');
  Object.assign(panel.style, {
    width: `${PANEL_WIDTH_PX}px`,
    maxHeight: 'calc(100vh - 96px)',
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(6,10,20,0.95)',
    border: '1px solid rgba(80,110,160,0.5)',
    borderRadius: '4px',
    color: '#d8e4ff',
    fontSize: '12px',
    lineHeight: '1.5',
    fontVariantNumeric: 'tabular-nums',
  });

  const titleBar = document.createElement('div');
  Object.assign(titleBar.style, {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 14px',
    borderBottom: '1px solid rgba(80,110,160,0.35)',
    background: 'rgba(30,50,90,0.45)',
    fontSize: '14px',
    fontWeight: '600',
    letterSpacing: '0.04em',
  });
  const title = document.createElement('span');
  title.textContent = 'RESEARCH';
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.textContent = '×';
  closeBtn.title = 'Close the Research panel. Hotkey: R or Esc.';
  Object.assign(closeBtn.style, {
    font: 'inherit',
    fontSize: '20px',
    lineHeight: '1',
    background: 'transparent',
    border: 'none',
    color: '#8ea0c8',
    cursor: 'pointer',
    padding: '0 6px',
  });
  closeBtn.addEventListener('pointerdown', () => close());
  titleBar.appendChild(title);
  titleBar.appendChild(closeBtn);

  const content = document.createElement('div');
  Object.assign(content.style, {
    padding: '12px 14px',
    overflow: 'auto',
    flex: '1 1 auto',
  });

  panel.appendChild(titleBar);
  panel.appendChild(content);
  backdrop.appendChild(panel);
  parent.appendChild(backdrop);

  // Click the backdrop (but not the panel itself) closes.
  backdrop.addEventListener('pointerdown', (ev) => {
    if (ev.target === backdrop) close();
  });

  // Panel content click delegation. pointerdown so the dispatch fires
  // before any per-tick re-render can replace the row DOM.
  //
  // MFix42.8c — two click targets now:
  //   (1) Tech nodes carry data-tech-name + data-tech-action with values
  //       'set' (activeResearch is null → start) or 'enqueue' (active is
  //       non-null → append to queue). 'none' / disabled nodes ignored.
  //   (2) Queue-list remove buttons carry data-queue-index + data-empire-id.
  content.addEventListener('pointerdown', (ev) => {
    const target = ev.target as Element | null;
    if (!target) return;

    // Queue-remove button takes priority — its DOM sits inside the
    // Queue section, not over a tech node.
    const removeBtn = target.closest('[data-queue-index]') as Element | null;
    if (removeBtn) {
      const indexStr = removeBtn.getAttribute('data-queue-index');
      const empIdStr = removeBtn.getAttribute('data-empire-id');
      const index = indexStr === null ? NaN : Number(indexStr);
      const empireId = empIdStr === null ? NaN : Number(empIdStr);
      if (!Number.isFinite(index) || !Number.isFinite(empireId)) return;
      options.queueCommand?.({
        kind: 'dequeueResearchTarget',
        empireId,
        index,
      });
      return;
    }

    // Tech-tree node. data-tech-action drives the dispatch shape so
    // the click handler doesn't need to re-derive activeResearch state.
    const node = target.closest('[data-tech-name]') as Element | null;
    if (!node || node.hasAttribute('data-disabled')) return;
    const techName = node.getAttribute('data-tech-name');
    const empireIdStr = node.getAttribute('data-empire-id');
    const action = node.getAttribute('data-tech-action');
    const empireId = empireIdStr === null ? NaN : Number(empireIdStr);
    if (!techName || !Number.isFinite(empireId)) return;
    if (action === 'set') {
      options.queueCommand?.({
        kind: 'setResearchTarget',
        empireId,
        techName,
      });
    } else if (action === 'enqueue') {
      options.queueCommand?.({
        kind: 'enqueueResearchTarget',
        empireId,
        techName,
      });
    }
    // 'none' (researched / active / already-queued) is a no-op — UI
    // should already render the node as non-actionable, and we
    // defend against stray clicks by ignoring unrecognised actions.
  });

  let openState = false;
  let lastRenderedKey = '';
  // MFix14 — split-key invariant for the hover-skip. `lastStructuralKey`
  // captures the pieces of the render key that mean "the DOM must
  // rebuild now" (researched-tech set size, active research target).
  // `lastRenderedKey` still captures everything including `state.tick`
  // for the cheap early-return on no-op ticks. The hover-skip from
  // MFix13.1d gates only on the tick-only delta; structural deltas
  // always rebuild so a click-then-select cycle isn't blocked by the
  // user's pointer remaining over the panel.
  let lastStructuralKey = '';

  // MFix13.1d — mirror the detail-panel hover-skip pattern (MFix11.1).
  // The tech-tree SVG nodes carry native `title` tooltips; when the panel
  // rebuilds mid-hover the tooltip tears off and re-attaches, producing
  // a perceptible flicker. Tracking pointer presence on `content` lets
  // `update()` short-circuit the rebuild while a tooltip is visible, at
  // the cost of freezing rp-accumulated counters during the hover —
  // an acceptable trade since the sim still advances normally.
  let isHovering = false;
  content.addEventListener('pointerover', () => {
    isHovering = true;
  });
  content.addEventListener('pointerout', (ev) => {
    // pointerout fires on every child-boundary crossing; only treat it
    // as "left the content" when relatedTarget is outside the subtree.
    const next = ev.relatedTarget as Node | null;
    if (next && content.contains(next)) return;
    isHovering = false;
  });

  const open = (): void => {
    if (openState) return;
    openState = true;
    backdrop.style.display = 'flex';
    lastRenderedKey = ''; // force re-render
    lastStructuralKey = ''; // pair with the lastRenderedKey reset
    getAudioEngine().playCue('ui:panel-open');
  };
  const close = (): void => {
    if (!openState) return;
    openState = false;
    backdrop.style.display = 'none';
    getAudioEngine().playCue('ui:panel-close');
  };
  const toggle = (): void => {
    if (openState) close();
    else open();
  };

  const onKeyDown = (ev: KeyboardEvent): void => {
    // Don't swallow keys while a text input has focus (future-proofing —
    // no text inputs exist yet, but buttons don't fire keydowns on space
    // so this is safe either way).
    const tag = (document.activeElement?.tagName ?? '').toLowerCase();
    if (tag === 'input' || tag === 'textarea') return;
    if (ev.key === 'r' || ev.key === 'R') {
      toggle();
      ev.preventDefault();
    } else if (ev.key === 'Escape' && openState) {
      close();
      ev.preventDefault();
    }
  };
  window.addEventListener('keydown', onKeyDown);

  return {
    update(state: GameState): void {
      if (!openState) return;
      const empire = state.empires.get(state.playerEmpireId);
      if (!empire) return;

      // Re-render key mirrors detail-panel's tick-based invalidation,
      // with researchedTechs size so the panel refreshes the moment a
      // tech completes. MFix14 — split into structural + tick parts so
      // the hover-skip below can let structural changes through while
      // suppressing the per-tick rp-accumulator update.
      //
      // MFix42.8c — extended with queue contents + parked-map size so
      // (a) enqueue/dequeue/promotion rebuilds the Queue section and
      // re-evaluates tech-node action attributes; (b) a parked-progress
      // delta (switch-away or restore-on-target) refreshes the "paused
      // at N RP" tooltip line on each tech node. Queue serialised as
      // join — order matters (promotion always pulls the head) so a
      // pure size-check would miss reorderings.
      const structuralKey =
        `${empire.researchedTechs.size}|${empire.activeResearch?.techName ?? 'none'}` +
        `|q:${empire.researchQueue.join(',')}` +
        `|p:${empire.parkedResearchProgress.size}`;
      const key = `${state.tick}|${structuralKey}`;
      if (key === lastRenderedKey) return;
      // MFix13.1d / MFix14 — hover-skip applies ONLY when the structural
      // key is unchanged. A structural delta (player just clicked a
      // tech, or a tech finished researching) MUST rebuild — otherwise
      // the panel keeps showing stale `data-disabled` attributes on
      // tech nodes that should now be clickable, and the user can't
      // research the next tech (MFix14 playtest bug).
      const structuralChanged = structuralKey !== lastStructuralKey;
      if (!structuralChanged && isHovering) return;
      lastRenderedKey = key;
      lastStructuralKey = structuralKey;

      renderContent(content, state, empire);
    },
    destroy(): void {
      window.removeEventListener('keydown', onKeyDown);
      backdrop.remove();
    },
    open,
    close,
    toggle,
    isOpen: () => openState,
  };
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

function renderContent(host: HTMLElement, state: GameState, empire: Empire): void {
  host.innerHTML = '';

  // Active research summary.
  host.appendChild(sectionHeader('Active'));
  if (empire.activeResearch === null) {
    host.appendChild(
      dim(
        empire.researchQueue.length > 0
          ? '— no active research — queue head will promote next tick —'
          : '— no active research — click a tech below to start —',
      ),
    );
  } else {
    const tech = state.techs.get(empire.activeResearch.techName);
    const cost = tech?.researchCost ?? 0;
    const active = document.createElement('div');
    active.style.padding = '4px 0';
    active.style.fontWeight = '600';
    active.textContent =
      `${empire.activeResearch.techName} · ${Math.floor(empire.activeResearch.rpAccumulated)}/${cost} RP` +
      (empire.resources.research > 0
        ? `  (pool ${Math.floor(empire.resources.research)} RP)`
        : '');
    host.appendChild(active);
  }

  // MFix42.8c — Queue section. Shown only when the queue has entries;
  // empty-queue case stays hidden to keep the panel tidy for solo-active
  // play (the typical case). Click a tech in the tree below to append.
  if (empire.researchQueue.length > 0) {
    host.appendChild(sectionHeader('Queue'));
    empire.researchQueue.forEach((techName, index) => {
      host.appendChild(renderQueueRow(state, empire, techName, index));
    });
  }

  // Tech tree DAG.
  host.appendChild(sectionHeader('Tech tree'));
  if (state.techs.size === 0) {
    host.appendChild(dim('— no techs loaded —'));
    return;
  }
  const layout = computeDagLayout(state.techs);
  const svg = buildDagSvg(layout, empire);
  host.appendChild(svg);
}

/**
 * Player-facing reason this tech can't be targeted right now. Returns null
 * if the tech is freely available (prereqs met, race matches, not already
 * researched, not already active). Already-researched and already-active
 * cases are handled by the caller via visual state, not this helper.
 */
function disabledReason(tech: TechDef, empire: Empire): string | null {
  if (tech.race !== 'All' && tech.race !== empire.raceId) {
    return `Race-locked to ${tech.race}`;
  }
  const missing = tech.prerequisites.filter((p) => !empire.researchedTechs.has(p));
  if (missing.length > 0) {
    return `Requires: ${missing.join(', ')}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Small shared style bits — duplicated from detail-panel.ts to keep this
// panel self-contained. If a third panel wants these, extract to ui/styles.ts.
// ---------------------------------------------------------------------------

function sectionHeader(label: string): HTMLElement {
  const h = document.createElement('div');
  h.textContent = label;
  h.style.marginTop = '14px';
  h.style.marginBottom = '4px';
  h.style.fontSize = '10px';
  h.style.letterSpacing = '0.08em';
  h.style.color = '#7a8bb5';
  h.style.borderBottom = '1px solid rgba(80,110,160,0.25)';
  h.style.paddingBottom = '2px';
  h.style.textTransform = 'uppercase';
  return h;
}

function dim(text: string): HTMLElement {
  const el = document.createElement('div');
  el.textContent = text;
  el.style.color = '#6e80a8';
  el.style.fontStyle = 'italic';
  el.style.padding = '4px 0';
  return el;
}

/**
 * MFix42.8c — One queue list row. Shows the queued tech name + cost,
 * with a small × button that dispatches `dequeueResearchTarget` via the
 * panel-level pointerdown delegation. Minimal reorder UI per scope —
 * the player removes + re-adds via the tree to move an entry. Parked
 * progress for the entry is surfaced inline when present.
 */
function renderQueueRow(
  state: GameState,
  empire: Empire,
  techName: string,
  index: number,
): HTMLElement {
  const row = document.createElement('div');
  Object.assign(row.style, {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '3px 0',
    color: '#bcd0f4',
  });

  const left = document.createElement('span');
  const tech = state.techs.get(techName);
  const cost = tech?.researchCost ?? 0;
  const parked = empire.parkedResearchProgress.get(techName);
  // "1. Standard Hull Mk 3 · 120 RP (paused at 40 RP)"
  const parkedSuffix =
    parked !== undefined && parked > 0
      ? `  (paused at ${Math.floor(parked)} RP)`
      : '';
  left.textContent = `${index + 1}. ${techName} · ${cost} RP${parkedSuffix}`;
  row.appendChild(left);

  const removeBtn = document.createElement('button');
  removeBtn.type = 'button';
  removeBtn.textContent = '×';
  removeBtn.title = `Remove ${techName} from the research queue.`;
  removeBtn.setAttribute('data-queue-index', String(index));
  removeBtn.setAttribute('data-empire-id', String(empire.id));
  Object.assign(removeBtn.style, {
    font: 'inherit',
    fontSize: '14px',
    lineHeight: '1',
    background: 'transparent',
    border: '1px solid rgba(120,140,180,0.4)',
    borderRadius: '3px',
    color: '#a0b4d8',
    cursor: 'pointer',
    padding: '2px 7px',
    marginLeft: '8px',
  });
  row.appendChild(removeBtn);

  return row;
}

// ---------------------------------------------------------------------------
// v3M23.1 — DAG layout
// ---------------------------------------------------------------------------

export interface NodeLayout {
  tech: TechDef;
  x: number;
  y: number;
}

export interface EdgeLayout {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface CategoryBand {
  category: string;
  y: number;
  height: number;
  /** y of the divider line drawn below this band, or null for the last band. */
  divider: number | null;
}

export interface DagLayout {
  nodes: NodeLayout[];
  edges: EdgeLayout[];
  categoryBands: CategoryBand[];
  width: number;
  height: number;
}

/**
 * Position every tech on a category × tier grid.
 *
 * Y axis: one band per category, in load order. When multiple techs share a
 * tier inside a category, they stack vertically and the band height grows to
 * fit. Categories never share rows.
 *
 * X axis: tech.level when defined; otherwise depth = max(prereq depth) + 1
 * (1 for techs with no prereqs). Cycle-guarded so a malformed prereq graph
 * doesn't recurse forever.
 */
export function computeDagLayout(techs: ReadonlyMap<string, TechDef>): DagLayout {
  const nodes: NodeLayout[] = [];
  const edges: EdgeLayout[] = [];
  const categoryBands: CategoryBand[] = [];
  const nodeByName = new Map<string, NodeLayout>();

  if (techs.size === 0) {
    return { nodes, edges, categoryBands, width: 0, height: 0 };
  }

  // Step 1: depth per tech (cached, cycle-guarded).
  const depthMap = new Map<string, number>();
  const visiting = new Set<string>();
  const depthOf = (name: string): number => {
    const cached = depthMap.get(name);
    if (cached !== undefined) return cached;
    if (visiting.has(name)) return 1;
    const tech = techs.get(name);
    if (!tech) return 1;
    visiting.add(name);
    let d: number;
    if (typeof tech.level === 'number') {
      d = tech.level;
    } else if (tech.prerequisites.length === 0) {
      d = 1;
    } else {
      let max = 0;
      for (const p of tech.prerequisites) {
        const pd = depthOf(p);
        if (pd > max) max = pd;
      }
      d = max + 1;
    }
    visiting.delete(name);
    depthMap.set(name, d);
    return d;
  };
  for (const tech of techs.values()) {
    depthOf(tech.techName);
  }

  // Step 2: bucket by category preserving load order.
  const byCategory = new Map<string, TechDef[]>();
  for (const tech of techs.values()) {
    let bucket = byCategory.get(tech.category);
    if (!bucket) {
      bucket = [];
      byCategory.set(tech.category, bucket);
    }
    bucket.push(tech);
  }

  // Step 3: max tier across the whole tree (used for SVG width).
  let maxTier = 1;
  for (const d of depthMap.values()) {
    if (d > maxTier) maxTier = d;
  }

  // Step 4: position nodes per category band.
  //
  // MFix13.1e — sub-category-aware bucketing. Within a category, techs
  // with a non-null `subCategory` each anchor to their own row across
  // all tiers; row order is first-seen-subCategory. Techs with
  // `subCategory: null` share a single default row that preserves the
  // pre-v4M5.1 tier-stack behaviour (vertical collision only when two
  // or more techs share both subCategory AND tier).
  //
  // Currently the only category that uses subCategories is
  // `'Survivability'` (Hull / Shield / Armor families, populated by
  // MFix13.1b/.1c). Every other category has every tech as
  // `subCategory: null`, so they all map to the single null family and
  // behave exactly as the pre-MFix11.2 tier-stack layout did.
  //
  // History: MFix11.2 attempted to fix this with a ` Mk \d+$` regex
  // family-name bucket; that split "Standard Hull Mk 1-3" and
  // "Reinforced Hull Mk 1-3" into two separate families and produced
  // worse alignment than no fix at all. Reverted at MFix13.1a; the
  // subCategory xlsx column (MFix13.1b/.1c) is the data-driven
  // replacement consumed here.
  const FAMILY_NULL = '__none__';
  let yCursor = SVG_PAD;
  for (const [category, techs] of byCategory) {
    // First-seen-subCategory order so a deterministic load order
    // produces a deterministic row layout. Iterating `techs` (already
    // in JSON-file order via byCategory's insertion-preserving Map)
    // gives the player Hull → Armor → Shield rows for Survivability.
    const familyOrder: string[] = [];
    const seenFamilies = new Set<string>();
    const byFamilyTier = new Map<string, Map<number, TechDef[]>>();
    for (const tech of techs) {
      const family = tech.subCategory ?? FAMILY_NULL;
      if (!seenFamilies.has(family)) {
        seenFamilies.add(family);
        familyOrder.push(family);
      }
      let famMap = byFamilyTier.get(family);
      if (!famMap) {
        famMap = new Map();
        byFamilyTier.set(family, famMap);
      }
      const t = depthMap.get(tech.techName) ?? 1;
      let bucket = famMap.get(t);
      if (!bucket) {
        bucket = [];
        famMap.set(t, bucket);
      }
      bucket.push(tech);
    }

    // Per-family vertical slot height = max stack across its tiers.
    // Single-tech-per-tier families collapse to one row (NODE_H);
    // multi-tech-per-tier-within-family families grow vertically.
    const familyHeight = new Map<string, number>();
    for (const family of familyOrder) {
      let maxStack = 1;
      const famMap = byFamilyTier.get(family)!;
      for (const bucket of famMap.values()) {
        if (bucket.length > maxStack) maxStack = bucket.length;
      }
      familyHeight.set(
        family,
        maxStack * NODE_H + (maxStack - 1) * ROW_GAP,
      );
    }

    let bandHeight = 0;
    for (const family of familyOrder) bandHeight += familyHeight.get(family)!;
    if (familyOrder.length > 1) {
      bandHeight += (familyOrder.length - 1) * ROW_GAP;
    }

    // Place each family's techs in its vertical slot, in turn.
    let familyYOffset = 0;
    for (const family of familyOrder) {
      const famMap = byFamilyTier.get(family)!;
      const famH = familyHeight.get(family)!;
      const famY = yCursor + familyYOffset;
      for (const [tier, techsInTier] of famMap) {
        techsInTier.sort((a, b) => a.techName.localeCompare(b.techName));
        const x = SVG_PAD + LABEL_W + (tier - 1) * (NODE_W + COL_GAP);
        const stackHeight =
          techsInTier.length * NODE_H + (techsInTier.length - 1) * ROW_GAP;
        // Centre the in-cell stack within the family's vertical slot,
        // matching the pre-MFix13.1e centring convention.
        const yStart = famY + (famH - stackHeight) / 2;
        techsInTier.forEach((tech, i) => {
          const node: NodeLayout = {
            tech,
            x,
            y: yStart + i * (NODE_H + ROW_GAP),
          };
          nodes.push(node);
          nodeByName.set(tech.techName, node);
        });
      }
      familyYOffset += famH + ROW_GAP;
    }

    categoryBands.push({
      category,
      y: yCursor,
      height: bandHeight,
      divider: yCursor + bandHeight + ROW_GAP,
    });
    yCursor += bandHeight + ROW_GAP * 2;
  }
  if (categoryBands.length > 0) {
    categoryBands[categoryBands.length - 1].divider = null;
  }

  // Step 5: prereq edges. Skip self/missing endpoints.
  for (const tech of techs.values()) {
    const to = nodeByName.get(tech.techName);
    if (!to) continue;
    for (const prereq of tech.prerequisites) {
      const from = nodeByName.get(prereq);
      if (!from || from === to) continue;
      edges.push({
        x1: from.x + NODE_W,
        y1: from.y + NODE_H / 2,
        x2: to.x,
        y2: to.y + NODE_H / 2,
      });
    }
  }

  const width =
    SVG_PAD + LABEL_W + (maxTier - 1) * (NODE_W + COL_GAP) + NODE_W + SVG_PAD;
  const height = yCursor + SVG_PAD;

  return { nodes, edges, categoryBands, width, height };
}

/**
 * Render a computed DAG layout to an `<svg>` element. Edges drawn first
 * so node rectangles paint over them. Each node is a `<g data-tech-name>`
 * group; the panel-level pointerdown delegation handles the click.
 */
function buildDagSvg(layout: DagLayout, empire: Empire): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg') as SVGSVGElement;
  svg.setAttribute('width', String(layout.width));
  svg.setAttribute('height', String(layout.height));
  svg.setAttribute('viewBox', `0 0 ${layout.width} ${layout.height}`);
  svg.style.display = 'block';

  // Arrow-head marker for prereq edges.
  const defs = document.createElementNS(SVG_NS, 'defs');
  const marker = document.createElementNS(SVG_NS, 'marker');
  marker.setAttribute('id', 'tech-arrow');
  marker.setAttribute('viewBox', '0 0 10 10');
  marker.setAttribute('refX', '10');
  marker.setAttribute('refY', '5');
  marker.setAttribute('markerWidth', '6');
  marker.setAttribute('markerHeight', '6');
  marker.setAttribute('orient', 'auto-start-reverse');
  const arrowPath = document.createElementNS(SVG_NS, 'path');
  arrowPath.setAttribute('d', 'M 0 0 L 10 5 L 0 10 z');
  arrowPath.setAttribute('fill', 'rgba(140, 170, 220, 0.6)');
  marker.appendChild(arrowPath);
  defs.appendChild(marker);
  svg.appendChild(defs);

  // Category labels and dividers.
  for (const band of layout.categoryBands) {
    const label = document.createElementNS(SVG_NS, 'text');
    label.setAttribute('x', String(SVG_PAD + 4));
    label.setAttribute('y', String(band.y + band.height / 2));
    label.setAttribute('dominant-baseline', 'middle');
    label.setAttribute('text-anchor', 'start');
    label.setAttribute('fill', '#7a8bb5');
    label.setAttribute('font-size', '10');
    label.setAttribute('letter-spacing', '0.08em');
    label.setAttribute('font-weight', '600');
    label.textContent = band.category.toUpperCase();
    svg.appendChild(label);

    if (band.divider !== null) {
      const div = document.createElementNS(SVG_NS, 'line');
      div.setAttribute('x1', String(SVG_PAD));
      div.setAttribute('x2', String(layout.width - SVG_PAD));
      div.setAttribute('y1', String(band.divider));
      div.setAttribute('y2', String(band.divider));
      div.setAttribute('stroke', 'rgba(80,110,160,0.25)');
      div.setAttribute('stroke-width', '1');
      svg.appendChild(div);
    }
  }

  // Edges (drawn before nodes so nodes overpaint endpoints cleanly).
  for (const edge of layout.edges) {
    const path = document.createElementNS(SVG_NS, 'path');
    const dx = Math.max((edge.x2 - edge.x1) * 0.5, 16);
    path.setAttribute(
      'd',
      `M ${edge.x1} ${edge.y1} ` +
        `C ${edge.x1 + dx} ${edge.y1}, ${edge.x2 - dx} ${edge.y2}, ${edge.x2} ${edge.y2}`,
    );
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'rgba(140, 170, 220, 0.45)');
    path.setAttribute('stroke-width', '1.2');
    path.setAttribute('marker-end', 'url(#tech-arrow)');
    svg.appendChild(path);
  }

  // Nodes.
  for (const node of layout.nodes) {
    svg.appendChild(buildDagNode(node, empire));
  }

  return svg;
}

/**
 * One tech node. Visual states mirror the prior list view (researched /
 * active / available / locked) and `data-tech-name` + `data-empire-id`
 * carry the click target through the panel-level delegation.
 */
function buildDagNode(node: NodeLayout, empire: Empire): SVGGElement {
  const { tech } = node;
  const isResearched = empire.researchedTechs.has(tech.techName);
  const isActive = empire.activeResearch?.techName === tech.techName;
  // MFix42.8c — queued state: tech is in the FIFO researchQueue.
  // Visually distinct from active (slightly paler tint), non-clickable
  // (click in the Queue list to remove). Queued + active are mutually
  // exclusive by construction (enqueue rejects when techName matches
  // activeResearch).
  const queueIndex = empire.researchQueue.indexOf(tech.techName);
  const isQueued = queueIndex !== -1;
  const parkedRp = empire.parkedResearchProgress.get(tech.techName);
  const reason = disabledReason(tech, empire);

  const g = document.createElementNS(SVG_NS, 'g') as SVGGElement;
  g.setAttribute('transform', `translate(${node.x}, ${node.y})`);
  g.setAttribute('data-tech-name', tech.techName);
  g.setAttribute('data-empire-id', String(empire.id));

  let fill = 'rgba(40, 60, 100, 0.45)';
  let stroke = 'rgba(80,110,160,0.55)';
  let strokeWidth = '1';
  let textColor = '#d8e4ff';
  let opacity = '1';
  let cursor = 'pointer';
  // MFix42.8c — drives the panel-level pointerdown delegation:
  //   'set'     → activeResearch is null + this node is freely available
  //   'enqueue' → activeResearch is non-null + this node is freely available
  //   'none'    → researched / active / queued / locked — click ignored
  let action: 'set' | 'enqueue' | 'none' = 'none';

  if (isResearched) {
    fill = 'rgba(40, 70, 50, 0.35)';
    stroke = 'rgba(120, 170, 130, 0.55)';
    textColor = '#bcd4be';
    opacity = '0.75';
    cursor = 'default';
  } else if (isActive) {
    fill = 'rgba(80, 140, 220, 0.35)';
    stroke = 'rgba(140, 190, 255, 0.85)';
    strokeWidth = '1.5';
    cursor = 'default';
  } else if (isQueued) {
    // Queued: paler blue than active, with the queue position in the
    // tooltip. Cursor 'default' (not 'pointer') signals non-actionable
    // from the tree — removal happens in the Queue list above.
    fill = 'rgba(80, 140, 220, 0.18)';
    stroke = 'rgba(140, 190, 255, 0.45)';
    cursor = 'default';
    g.setAttribute('data-disabled', 'true');
  } else if (reason !== null) {
    fill = 'rgba(40, 60, 100, 0.25)';
    stroke = 'rgba(80,110,160,0.3)';
    opacity = '0.5';
    cursor = 'not-allowed';
    g.setAttribute('data-disabled', 'true');
  } else {
    // Freely available — action depends on whether the player already
    // has an active target. Queue add when busy; direct set when idle.
    action = empire.activeResearch === null ? 'set' : 'enqueue';
  }
  g.setAttribute('data-tech-action', action);

  const rect = document.createElementNS(SVG_NS, 'rect');
  rect.setAttribute('width', String(NODE_W));
  rect.setAttribute('height', String(NODE_H));
  rect.setAttribute('rx', '3');
  rect.setAttribute('ry', '3');
  rect.setAttribute('fill', fill);
  rect.setAttribute('stroke', stroke);
  rect.setAttribute('stroke-width', strokeWidth);
  rect.setAttribute('opacity', opacity);
  g.appendChild(rect);

  // v4M1.11 — hover lift on available tech nodes. Researched / active /
  // disabled / queued nodes don't respond to hover so the cursor:
  // pointer state already telegraphs whether the node is clickable.
  // MFix42.8c — `action === 'none'` is the unified check: covers
  // researched, active, queued, and reason-blocked in one branch.
  if (action !== 'none') {
    const baseFill = fill;
    const hoverFill = 'rgba(80, 130, 200, 0.55)';
    const baseStroke = stroke;
    const hoverStroke = 'rgba(180, 220, 255, 0.85)';
    g.addEventListener('mouseenter', () => {
      rect.setAttribute('fill', hoverFill);
      rect.setAttribute('stroke', hoverStroke);
    });
    g.addEventListener('mouseleave', () => {
      rect.setAttribute('fill', baseFill);
      rect.setAttribute('stroke', baseStroke);
    });
  }

  const nameText = document.createElementNS(SVG_NS, 'text');
  nameText.setAttribute('x', '8');
  nameText.setAttribute('y', '15');
  nameText.setAttribute('fill', textColor);
  nameText.setAttribute('font-size', '11');
  nameText.setAttribute('font-weight', '600');
  nameText.setAttribute('opacity', opacity);
  nameText.textContent = (isResearched ? '✓ ' : '') + truncateText(tech.techName, 22);
  g.appendChild(nameText);

  const costText = document.createElementNS(SVG_NS, 'text');
  costText.setAttribute('x', '8');
  costText.setAttribute('y', '29');
  costText.setAttribute('fill', '#8ea0c8');
  costText.setAttribute('font-size', '10');
  costText.setAttribute('opacity', opacity);
  costText.textContent = tech.researchCost === 0 ? 'free' : `${tech.researchCost} RP`;
  g.appendChild(costText);

  g.style.cursor = cursor;

  // v4M1.2 — every node gets a hover tooltip. State-dependent text:
  //   researched → "Already researched"
  //   active     → "Currently researching: <accumulated> / <cost> RP"
  //   queued     → "Queued at position N — remove via Queue list above"
  //                (MFix42.8c)
  //   disabled   → existing prereq-or-race reason
  //   available  → click-prompt with cost + category. When activeResearch
  //                is non-null, click appends to queue instead of
  //                replacing current target (MFix42.8c).
  //   parked     → appended as a " (paused at N RP)" suffix to every
  //                state where the node has parked progress, so the
  //                player can see at-a-glance whether resuming this
  //                tech will skip ahead (MFix42.8c).
  const titleEl = document.createElementNS(SVG_NS, 'title');
  const parkedSuffix =
    parkedRp !== undefined && parkedRp > 0 && !isResearched && !isActive
      ? ` (paused at ${Math.floor(parkedRp)} RP)`
      : '';
  if (isResearched) {
    titleEl.textContent = `${tech.techName} — researched. ${tech.category} category.`;
  } else if (isActive) {
    const acc = Math.floor(empire.activeResearch?.rpAccumulated ?? 0);
    titleEl.textContent =
      `${tech.techName} — researching. ${acc} / ${tech.researchCost} RP. ${tech.category} category.`;
  } else if (isQueued) {
    titleEl.textContent =
      `${tech.techName} — queued at position ${queueIndex + 1}. Remove via the Queue list above.${parkedSuffix}`;
  } else if (reason !== null) {
    titleEl.textContent = `${tech.techName} — ${reason}${parkedSuffix}`;
  } else if (action === 'enqueue') {
    titleEl.textContent =
      `${tech.techName} — click to append to research queue. Cost ${tech.researchCost} RP. ${tech.category} category.${parkedSuffix}`;
  } else {
    titleEl.textContent =
      `${tech.techName} — click to start research. Cost ${tech.researchCost} RP. ${tech.category} category.${parkedSuffix}`;
  }
  g.appendChild(titleEl);

  return g;
}

function truncateText(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}
